import { useState, useCallback, useEffect } from 'react'
import Joyride, { Step, CallBackProps, STATUS, EVENTS } from 'react-joyride'
import { useAdminStore } from '@/store/adminStore'
import { useTranslation } from 'react-i18next'

const TourContent = ({ title, content }: { title: string; content: string }) => (
  <div className="tour-content flex items-start gap-4 p-2">
    <div className="flex-1">
      <h2 className="text-lg font-semibold mb-2 text-[#525FE1]">{title}</h2>
      <p className="text-gray-600 text-sm leading-relaxed">{content}</p>
    </div>
  </div>
)

export const TimetableNavigationTour = () => {
  const [, setStepIndex] = useState(0)
  const { navTour, completeNavTour, setNavStep, nextNavStep } = useAdminStore()
  const [isRunning, setIsRunning] = useState(false)
  const { t } = useTranslation('tour')
  // Start tour when timetableTour.isActive changes
  useEffect(() => {
    if (navTour.isActive && !isRunning) {
      setIsRunning(true)
    }
  }, [navTour.isActive, isRunning])
  //  if (finishedStatuses.includes(status)) {
  //      setIsRunning(false)
  const steps: Step[] = [
    {
      target: 'body',
      content: t('admin.timetableNavigationTour.steps.welcome.content'),
      disableBeacon: true,
      placement: 'center',
    },
    {
      target: '[data-tour="nav-general"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.general.title')}
        content={t('admin.timetableNavigationTour.steps.general.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-periods"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.periods.title')}
        content={t('admin.timetableNavigationTour.steps.periods.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-subjects"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.subjects.title')}
        content={t('admin.timetableNavigationTour.steps.subjects.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-teachers"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.teachers.title')}
        content={t('admin.timetableNavigationTour.steps.teachers.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-classes"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.classes.title')}
        content={t('admin.timetableNavigationTour.steps.classes.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-classrooms"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.classrooms.title')}
        content={t('admin.timetableNavigationTour.steps.classrooms.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-lessons"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.lessons.title')}
        content={t('admin.timetableNavigationTour.steps.lessons.content')}
      />,
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-generate"]',
      content: <TourContent 
        title={t('admin.timetableNavigationTour.steps.generate.title')}
        content={t('admin.timetableNavigationTour.steps.generate.content')}
      />,
      disableBeacon: true,
    }
  ]

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const {  index, status, type } = data

    if (type === EVENTS.STEP_AFTER) {
      setStepIndex(index + 1)
      nextNavStep()
    }
    
    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      setIsRunning(false)
      completeNavTour()
    } else if (type === EVENTS.STEP_BEFORE) {
      setStepIndex(index)
      setNavStep(index)
    }
  }, [completeNavTour, setNavStep, nextNavStep])

  if (!navTour?.isActive) {
    return null
  }

  return (
    <>
    <Joyride
    disableOverlayClose={true}
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      hideBackButton
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      run={isRunning}
      stepIndex={navTour.currentStep}
      styles={{
        options: {
          primaryColor: '#ffffff',
          zIndex: 10000,
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
        },
        buttonNext: {
          backgroundColor: '#ffffff',
          color: '#525FE1',
          fontSize: '16px',
          padding: '10px 20px',
          borderRadius: '6px',
        },
        buttonBack: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
          marginRight: '12px',
        },
        buttonSkip: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
        },
        tooltip: {
          padding: '24px',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          color: '#333',
          fontSize: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          minWidth: '300px',
          pointerEvents: 'auto' as const,
          transition: 'opacity 0.2s ease',
        },
        tooltipContainer: {
          textAlign: 'left' as const,
        },
        spotlight: {
          backgroundColor: 'transparent', // Completely transparent
          border: '2px solid white', // White border
          borderRadius: '8px',
          boxShadow: 'none', // Remove box shadow since we handle it in CSS
        },
      }}
      locale={{
        back: t('admin.timetableNavigationTour.locale.back'),
        close: t('admin.timetableNavigationTour.locale.close'),
        last: t('admin.timetableNavigationTour.locale.last'),
        next: t('admin.timetableNavigationTour.locale.next'),
        skip: t('admin.timetableNavigationTour.locale.skip'),
      }}
    />
    
    {/* Dark overlay for consistent darkening */}
    {isRunning && navTour.currentStep === 0 && (
      <div 
        className="fixed inset-0 bg-black/70 z-[9999] pointer-events-none"
        style={{ transition: 'opacity 0.2s ease' }}
      />
    )}
  </>
  )
} 